import React, { useState, useEffect } from 'react';
import { Lightbulb } from 'lucide-react';
import { HINT_TEXT } from '../../constants';

export interface HintData {
  text: string;
  emoji?: string;
  category?: string;
  additionalInfo?: string;
}

export interface ShowHintProps {
  hint: HintData;
  buttonText?: {
    show: string;
    hide: string;
  };
  disabled?: boolean;
  onHintToggle?: (isVisible: boolean) => void;
}

export const ShowHint: React.FC<ShowHintProps> = ({
  hint,
  buttonText = { show: HINT_TEXT.SHOW_HINT, hide: HINT_TEXT.HIDE_HINT },
  disabled = false,
  onHintToggle
}) => {
  const [showHint, setShowHint] = useState(false);

  // Auto-close hint after 1 second when opened
  useEffect(() => {
    if (showHint) {
      const timer = setTimeout(() => {
        setShowHint(false);
        onHintToggle?.(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showHint, onHintToggle]);

  const toggleHint = () => {
    const newState = !showHint;
    setShowHint(newState);
    onHintToggle?.(newState);
  };

  return (
    <>
      {showHint && (
        <div className="fixed bottom-24 right-6 z-40 max-w-xs animate-fadeIn">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-2xl p-4 shadow-2xl relative">
            <div className="absolute -bottom-3 right-6 w-6 h-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-r-2 border-b-2 border-yellow-200 transform rotate-45"></div>

            <div className="space-y-2">
              {hint.category && (
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-orange-600" />
                  <span className="text-orange-800 font-bold text-sm">{hint.category}:</span>
                </div>
              )}

              <p className="text-gray-800 text-sm leading-relaxed font-medium">
                {hint.emoji && <span className="mr-2">{hint.emoji}</span>}
                {hint.text}
              </p>

              {hint.additionalInfo && (
                <p className="text-gray-600 text-xs italic mt-2 pt-2 border-t border-yellow-200">
                  {hint.additionalInfo}
                </p>
              )}
            </div>

            <button
              onClick={toggleHint}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs font-bold hover:bg-red-600 transition-colors duration-200 flex items-center justify-center"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <button
        onClick={toggleHint}
        disabled={disabled}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 text-white rounded-full shadow-2xl transition-all duration-300 flex items-center justify-center ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:scale-110 hover:shadow-3xl active:scale-95'
        } ${showHint ? 'ring-4 ring-yellow-300 ring-opacity-50' : ''}`}
      >
        <Lightbulb className="w-6 h-6" />
      </button>
    </>
  );
};

export default ShowHint;